/**
 * Hook wrapping the generated Apollo mutation for transferring
 * manual player data (reports and notes) to a provider-sourced player.
 *
 * Exposes a simplified `transfer` function that accepts source/target IDs
 * and returns a promise. Handles cache invalidation by refetching the
 * source player query so report/note counts update to zero.
 * @module features/player-detail/hooks/useTransferManualPlayerData
 */

import { useCallback } from "react";

import {
  usePlayerDetailTransferManualPlayerDataMutation,
  type TransferManualPlayerDataInput,
} from "@/generated/graphql";

/**
 * Queries to refetch after a successful transfer so counts go to zero.
 * Includes both the player detail query (scout reports, transferable counts)
 * and the notes query (fetched separately by NotesSection).
 */
const REFETCH_QUERIES: readonly string[] = [
  "PlayerDetailGetPlayer",
  "NotesListNotesByPlayerId",
  "PlayerSearchSearchPlayers",
  "PlayerSearchFilterPlayers",
];

/**
 * Wraps the generated Apollo mutation with a simplified API.
 * Returns a stable `transfer` function that resolves on success
 * and rejects on failure, plus Apollo loading/error state.
 * @returns Transfer mutation API with simplified transfer function
 */
export const useTransferManualPlayerData = () => {
  const [mutate, { loading, error }] =
    usePlayerDetailTransferManualPlayerDataMutation({
      refetchQueries: [...REFETCH_QUERIES],
      awaitRefetchQueries: true,
    });

  const transfer = useCallback(
    (input: TransferManualPlayerDataInput) =>
      mutate({ variables: { input } }).then(result => {
        const data = result.data?.transferManualPlayerData;

        if (!data) {
          throw new Error("Transfer failed: no data returned");
        }

        return data;
      }),
    [mutate]
  );

  return { transfer, loading, error };
};
